<script>
  import { onMount } from "svelte";
  import { messages } from "./i18n/messages.js";

  // ---------- THEME ----------
  let theme = "dark"; // default

  function applyTheme(nextTheme) {
    if (typeof document === "undefined") return;

    const body = document.body;
    body.classList.remove("theme-light", "theme-dark");
    body.classList.add(nextTheme === "light" ? "theme-light" : "theme-dark");

    try {
      localStorage.setItem("theme", nextTheme);
    } catch (e) {}
  }

  function setTheme(nextTheme) {
    theme = nextTheme;
    applyTheme(nextTheme);
  }

  onMount(() => {
    let initial = "dark";

    try {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") {
        initial = stored;
      } else if (window.matchMedia?.("(prefers-color-scheme: light)").matches) {
        initial = "light";
      }
    } catch (e) {
      // ignore
    }

    setTheme(initial);
  });

  // kies logo + copy icon obv theme
  $: logoSrc =
    theme === "light" ? "/img/logo_10110_white.svg" : "/img/logo_10110.svg";

  $: copyIconSrc = theme === "light" ? "/img/copy.svg" : "/img/copy-white.svg";

  // ---------- I18N ----------
  let lang = "en"; // "en" of "fa"
  $: t = messages[lang] || messages.en;

  let referrerLink = `https://www.instagram.com/amirfahimnia/`;

  // --- Document sync (html lang/dir + title/description + og:locale) ---
  const DEFAULT_TITLE =
    "A software designer and engineer. Makes modest websites.";
  const DEFAULT_DESC =
    "Software design and engineering. Modest websites with sharp details.";

  function setMeta(name, content) {
    if (typeof document === "undefined" || !content) return;
    const el = document.querySelector(`meta[name="${name}"]`);
    if (el) el.setAttribute("content", content);
  }

  function setProp(property, content) {
    if (typeof document === "undefined" || !content) return;
    const el = document.querySelector(`meta[property="${property}"]`);
    if (el) el.setAttribute("content", content);
  }

  function syncDocumentLanguage(nextLang) {
    if (typeof document === "undefined") return;

    const isFa = nextLang === "fa";
    document.documentElement.lang = isFa ? "fa" : "en";
    document.documentElement.dir = isFa ? "rtl" : "ltr";

    // keeps things English by default, but allows per-lang overrides if you add them in messages
    const seoTitle = messages[nextLang]?.seoTitle || DEFAULT_TITLE;
    const seoDesc = messages[nextLang]?.seoDescription || DEFAULT_DESC;

    document.title = seoTitle;
    setMeta("description", seoDesc);

    // neat hygiene
    setProp("og:locale", isFa ? "fa_IR" : "en_US");
  }

  function trackLanguageSwitch(nextLang) {
    try {
      window.gtag?.("event", "language_switch", {
        language: nextLang
      });
    } catch (e) {}
  }

  // call on first mount
  onMount(() => {
    syncDocumentLanguage(lang);
  });

  // call on every lang change
  $: if (lang) {
    syncDocumentLanguage(lang);
    trackLanguageSwitch(lang);
  }

  // hover image near cursor
  let hoverImageIndex = -1;
  let showHoverImage = false;
  let mouseX = 0;
  let mouseY = 0;

  const hoverImages = [
    "/img/amir-fahimnia-10110.jpg",
    "/img/amir-fahimnia-lecturer.jpg",
    "/img/amir-fahimnia-speaking.jpg"
  ];

  const updateMouse = (event) => {
    mouseX = event.pageX + 20;
    mouseY = event.pageY + 20;
  };

  const handleMouseEnter = (event) => {
    hoverImageIndex = (hoverImageIndex + 1) % hoverImages.length;
    updateMouse(event);
    showHoverImage = true;
    window.addEventListener("mousemove", updateMouse, { passive: true });
  };

  const handleMouseLeave = () => {
    showHoverImage = false;
    window.removeEventListener("mousemove", updateMouse);
  };

  // marquee images
  let allImages = [];
  let topImages = [];
  let bottomImages = [];

  onMount(async () => {
    try {
      const response = await fetch("./images.json");
      const data = await response.json();
      allImages = data;

      topImages = allImages.slice(0, 58);
      bottomImages = allImages.slice(58, 116);
    } catch (e) {
      console.error("Fout bij ophalen afbeeldingen:", e);
    }
  });

  // time + binary
  let lastUpdated = "";
  let time = "";
  let timeZone = "";

  const updateBinaryCode = () => {
    lastUpdated = Array.from({ length: 6 }, () =>
      Math.floor(Math.random() * 2)
    ).join("");
  };

  const updateTime = () => {
    const now = new Date();
    const options = {
      timeZone: "Europe/Amsterdam",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short"
    };

    const timeWithZone = now.toLocaleString("en-US", options);
    const [timePart, zonePart] = timeWithZone.split(" ");
    time = timePart;
    timeZone = zonePart;
  };

  const tick = () => {
    updateBinaryCode();
    updateTime();
  };

  onMount(() => {
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  });

  // UTM
  let utmSource = "";
  let utmMedium = "";
  let utmCampaign = "";

  const updateUTMParams = () => {
    if (typeof window === "undefined") return;

    const urlParams = new URLSearchParams(window.location.search);
    utmSource = urlParams.get("utm_source") || "No source";
    utmMedium = urlParams.get("utm_medium") || "No medium";
    utmCampaign = urlParams.get("utm_campaign") || "No campaign";
  };

  onMount(() => updateUTMParams());

  // slogans
  let currentSlogan = "";
  onMount(() => {
    const list = t.slogans || [];
    currentSlogan = list[Math.floor(Math.random() * list.length)] || "";
  });

  $: if (t && t.slogans) {
    currentSlogan = t.slogans[Math.floor(Math.random() * t.slogans.length)];
  }

  // modal
  let showModal = false;
  const openModal = () => (showModal = true);
  const closeModal = () => (showModal = false);

  // lightbox for marquee click
  let lightboxOpen = false;
  let lightboxSrc = "";

  function openLightbox(src) {
    lightboxSrc = src;
    lightboxOpen = true;
  }
  function closeLightbox() {
    lightboxOpen = false;
    lightboxSrc = "";
  }

  function makeDrag() {
    return {
      el: null,
      isDown: false,
      moved: false,
      pointerId: null,
      startX: 0,
      startScroll: 0,

      onDown(e) {
        if (e.button !== undefined && e.button !== 0) return;

        this.isDown = true;
        this.moved = false;
        this.pointerId = e.pointerId;

        this.el?.classList.add("is-dragging");
        this.startX = e.clientX;
        this.startScroll = this.el.scrollLeft;

        this.el.style.animationPlayState = "paused";

        e.preventDefault();

        this.el.setPointerCapture?.(e.pointerId);

        window.addEventListener("pointermove", this._onWinMove, {
          passive: false
        });
        window.addEventListener("pointerup", this._onWinUp, { passive: true });
        window.addEventListener("pointercancel", this._onWinUp, {
          passive: true
        });
      },

      onMove(e) {
        if (!this.isDown) return;
        if (this.pointerId !== null && e.pointerId !== this.pointerId) return;

        const dx = e.clientX - this.startX;

        if (Math.abs(dx) > 4) this.moved = true;

        this.el.scrollLeft = this.startScroll - dx;

        e.preventDefault();
      },

      onUp() {
        this.isDown = false;
        this.pointerId = null;

        this.el?.classList.remove("is-dragging");
        this.el.style.animationPlayState = "";

        window.removeEventListener("pointermove", this._onWinMove);
        window.removeEventListener("pointerup", this._onWinUp);
        window.removeEventListener("pointercancel", this._onWinUp);
      },

      _onWinMove: null,
      _onWinUp: null,

      init() {
        this._onWinMove = (e) => this.onMove(e);
        this._onWinUp = () => this.onUp();
        return this;
      }
    }.init();
  }

  const dragTop = makeDrag();
  const dragBottom = makeDrag();
</script>

<main {lang} dir={lang === "fa" ? "rtl" : "ltr"}>
  <!-- overlay for your modal -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="overlay"
    on:click={closeModal}
    style:display={showModal ? "block" : "none"}
  ></div>

  <header>
    <div class="menu">
      <div>
        <img src={logoSrc} alt="Logo 10110" class="logo" />
        <!-- <img
          src="/img/instagram-verification-icon.png"
          alt="Verified badges on Instagram"
          width="18"
          height="18"
          class="insta-verify"
        /> -->
      </div>

      <div class="switch">
        <div class="lang-switch">
          <button class:selected={lang === "en"} on:click={() => (lang = "en")}>
            English
          </button>
          <button
            class="lang-switch__btn--fa"
            class:selected={lang === "fa"}
            on:click={() => (lang = "fa")}
          >
            فارسی
          </button>
        </div>
        <button
          type="button"
          class="theme-toggle"
          on:click={() => setTheme(theme === "light" ? "dark" : "light")}
          aria-label="Toggle theme"
        >
          <span class="theme-toggle__track">
            {#if theme === "dark"}
              <!-- dark mode: toon zon rechts -->
              <img
                src="/img/icon-sun.svg"
                alt="Switch to light mode"
                class="theme-toggle__icon theme-toggle__icon--right"
              />
            {:else}
              <!-- light mode: toon maan links -->
              <img
                src="/img/icon-moon.svg"
                alt="Switch to dark mode"
                class="theme-toggle__icon theme-toggle__icon--left"
              />
            {/if}

            <span
              class={"theme-toggle__thumb" +
                (theme === "light" ? " theme-toggle__thumb--light" : "")}
            ></span>
          </span>
        </button>
      </div>
    </div>

    <div class="">
      <span class="binary-code">{lastUpdated}</span>
      <h1 class="name">{t.tagline}</h1>
    </div>
  </header>

  <section>
    <!-- <div class="banner top-banner">
      <div
        class="marquee marquee-rtl"
        bind:this={dragTop.el}
        on:pointerdown|capture={(e) => dragTop.onDown(e)}
        on:pointermove={(e) => dragTop.onMove(e)}
        on:pointerup={() => dragTop.onUp()}
        on:pointercancel={() => dragTop.onUp()}
      >
        <div class="track">
          {#each topImages as src (src)}
            <button
              class="imgbtn"
              type="button"
              on:click={() => openLightbox(src)}
            >
              <img {src} alt="Artist image" loading="lazy" decoding="async" />
            </button>
          {/each}
        </div>

        <div class="track" aria-hidden="true">
          {#each topImages as src (src + "-dup")}
            <button
              class="imgbtn"
              type="button"
              on:click={() => openLightbox(src)}
            >
              <img {src} alt="" loading="lazy" decoding="async" />
            </button>
          {/each}
        </div>
      </div>
    </div> -->

    <!-- <div class="banner bottom-banner">
      <div
        class="marquee marquee-ltr"
        bind:this={dragBottom.el}
        on:pointerdown|capture={(e) => dragBottom.onDown(e)}
        on:pointermove={(e) => dragBottom.onMove(e)}
        on:pointerup={() => dragBottom.onUp()}
        on:pointercancel={() => dragBottom.onUp()}
      >
        <div class="track">
          {#each bottomImages as src (src)}
            <button
              class="imgbtn"
              type="button"
              on:click={() => {
                if (dragTop.moved || dragBottom.moved) return;
                openLightbox(src);
              }}
            >
              <img {src} alt="Artist image" loading="lazy" decoding="async" />
            </button>
          {/each}
        </div>

        <div class="track" aria-hidden="true">
          {#each bottomImages as src (src + "-dup")}
            <button
              class="imgbtn"
              type="button"
              on:click={() => openLightbox(src)}
            >
              <img {src} alt="" loading="lazy" decoding="async" />
            </button>
          {/each}
        </div>
      </div>
    </div> -->

    <!-- CONTEXT SECTION -->

    <!-- <h2>{t.h2_context}</h2> -->
    <p>{t.p_context_1}<br />{t.p_context_1_extra}</p>
    <p>{t.p_context_1_1}</p>
    <p>
      {t.p_context_2}
    </p>
    <p>
      {t.p_context_3}<br />{t.p_context_3_extra}<br />{t.p_context_3_extra_2}
    </p>

    <!-- PROTECTION SECTION -->

    <h2>{t.h3_protection}</h2>
    <p>{t.p_protection_1}<br />{t.p_protection_1_extra}</p>
    <p>{t.p_protection_2}<br />{t.p_protection_2_extra}</p>
    <p>{t.p_protection_3}</p>

    <p>{t.p_protection_4}</p>

    <p>{t.p_protection_5}<br />{t.p_protection_5_extra}</p>

    <!-- ABOUT AMIR SECTION -->

    <div class="code-container">
      <a href={referrerLink} target="_blank" rel="noopener">
        <img
          src="/img/amir-fahimnia-instagram-avatar.png"
          alt="amir fahimnia avatar"
          width="60"
          height="60"
          class="animate amir-avatar"
        />
      </a>

      <p class="m-1">{t.p_context}<br />{t.p_context_extra_1}</p>
      <p>
        {t.intro_prefix_context}<br />
        <a href={referrerLink} target="_blank" rel="noopener">
          <p class="intro">
            {t.intro_prefix_name}
            {" "}
            <img
              src="/img/link-blue.svg"
              alt="copy icon"
              width="10"
              height="10"
            />
          </p>
        </a>
      </p>

      <p>
        {t.p1}<br />{t.p2_extra_1}

        {#if showHoverImage}
          <img
            src={hoverImages[hoverImageIndex]}
            alt="Amir Fahimnia"
            class="hover-image"
            style="top: {mouseY}px; left: {mouseX}px;"
          />
        {/if}
      </p>
    </div>

    <!-- INFO SECTION -->
    <h3>{t.h3_info}</h3>
    <p>{t.p_info}</p>
    <p>{t.p_info_1}</p>
    <p>{t.p_info_2}</p>

    <!-- FINAL SECTION -->

    <h3>{t.h3_final}</h3>
    <p>{t.p_final}<br />{t.p_final_1_extra}<br />{t.p_final_1_extra_2}</p>

    <div class="links-footer">
      <p>
        <a
          href="mailto:info@10110.dev?&subject=Inquiries%20from%2010110 website"
          class="email"
          title="Send inquirie via email"
          aria-label="Click to send an inquiry email"
          ><code>info@10110.dev</code></a
        >
        <img src="/img/link.svg" alt="copy icon" width="10" height="10" />
      </p>
      <p>
        <a
          href="https://instagram.com/amirfahimnia"
          target="_blank"
          class="email"
          aria-label="Instagram profile link"><code>Instagram</code></a
        >
        <img src="/img/link.svg" alt="copy icon" width="10" height="10" />
      </p>
    </div>

    <p class="important-cta">{t.important_cta}</p>
  </section>

  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <img
    src="/img/farsi_speaking_icon.svg"
    alt="Farsi icon"
    class="floating-image animate"
    on:click={openModal}
  />

  <!-- Your Modal -->
  {#if showModal}
    <div id="myModal" class="modal">
      <div class="modal-content">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <span class="close-btn" on:click={closeModal}>&times;</span>
        <section>
          <p class="persian">{t.modal_text}</p>
          <div class="cta">
            <a href={referrerLink} target="_blank" rel="noopener">
              <img
                src="/img/amir-fahimnia-instagram-avatar.png"
                alt="amir fahimnia avatar"
                width="50"
                height="50"
                class="cta-image"
              />
            </a>
            <a
              href="mailto:info@10110.dev"
              target="_blank"
              class="modal-button"
            >
              {t.modal_button}
            </a>
          </div>
        </section>
      </div>
    </div>
  {/if}

  <!-- Lightbox -->
  {#if lightboxOpen}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="lightbox"
      role="dialog"
      aria-modal="true"
      on:click={closeLightbox}
    >
      <!-- svelte-ignore a11y-img-redundant-alt -->
      <img class="lightbox-img" src={lightboxSrc} alt="Selected image" />
    </div>
  {/if}

  <footer class="site-footer">
    <div class="footer-left">
      <span>
        ©{t.footer_year}
        <br />
        {t.footer_city} <span class="footer-sep">|</span>
      </span>

      <span class="time">
        <br />
        {time.slice(0, 2)}<span class="blink">:</span>{time.slice(3)}
        {t.footer_timeOffset}
      </span>
    </div>

    <a
      class="footer-right"
      href="https://instagram.com/amirfahimnia"
      target="_blank"
      rel="noreferrer"
    >
      <div class="version">
        <a href="https://instagram.com/amirfahimnia" target="_blank"
          >{t.footer_version}</a
        >
      </div>
    </a>
  </footer>
</main>

<style>
  :global(body) {
    margin: 0;
    padding: 0;
    background-color: var(--backgroundColor);
    color: var(--textColor);
    font-size: 1.2em;
    font-family: var(--sansSerif);
    line-height: 1.62em;
    word-wrap: break-word;
  }

  .name-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* BANNER + MARQUEE (new) */
  .banner {
    position: relative;
    width: 100vw;
    margin-left: -50%;
    height: 90px;
    overflow: hidden;
  }

  [dir="rtl"] .amir-avatar {
    left: 17px !important;
    right: unset !important;
  }

  [dir="rtl"] .binary-code {
    direction: ltr !important;
  }

  /* farsi / rtl */
  [dir="rtl"] .banner {
    margin-left: 0;
    margin-right: -50%;
  }

  [dir="rtl"] .based,
  [dir="rtl"] .based-footer {
    right: unset !important;
    left: 20px;
  }

  [dir="rtl"] h1,
  [dir="rtl"] h2,
  [dir="rtl"] h3,
  [dir="rtl"] p {
    font-family: "Vazir", sans-serif;
  }

  .top-banner {
    margin-top: 1.5rem;
  }
  .bottom-banner {
    margin-bottom: 1.5rem;
  }

  .marquee {
    display: flex;
    height: 100%;
    align-items: center;
    --speed: 200s;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    cursor: grab;
    touch-action: pan-y;
    user-select: none;
    contain: layout paint;
    direction: ltr;
    text-align: left;
  }
  .marquee::-webkit-scrollbar {
    display: none;
  }

  .track {
    display: flex;
    align-items: center;
    height: 100%;
    gap: 20px;
    flex: 0 0 auto;
    will-change: transform;
    padding-left: 1.2rem;
  }

  .imgbtn {
    border: 0;
    padding: 0;
    background: transparent;
    display: inline-flex;
    height: 100%;
    align-items: center;
  }

  /* .track img {
    width: auto;
    height: 70%;
    display: block;
    transition: transform 0.2s;
    -webkit-user-drag: none;
    box-shadow: 2px 4px 8px #00000045;
    border-radius: 50%;
  }

  .track img:hover {
    transform: scale(1.3);
    box-shadow: 1px 1px 2px #00000045;
  } */

  /* animation on the track; seamless loop via double track */
  /* .marquee-rtl .track {
    animation: rtl var(--speed, 60s) linear infinite;
  }
  @keyframes rtl {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }

  .marquee-ltr .track {
    animation: ltr var(--speed, 70s) linear infinite;
  }
  @keyframes ltr {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  } */

  /* LIGHTBOX */
  .lightbox {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    display: grid;
    place-items: center;
    z-index: 10000;
    padding: 20px;
  }
  .lightbox-img {
    max-width: min(1100px, 92vw);
    max-height: 88vh;
    border-radius: 14px;
  }

  /* your existing styles below, mostly unchanged */
  header {
    margin-bottom: 1em;
  }

  .menu {
    display: flex;
    justify-content: space-between;
    align-items: center;
    direction: ltr;
    text-align: left;
  }

  .insta-verify {
    position: absolute;
    top: -0.7rem;
    margin-left: 0.5rem;
  }

  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(2px);
    z-index: 9998;
  }

  .lang-switch {
    display: flex;
    gap: 0.5rem;
  }

  .lang-switch button {
    border: none;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    font-size: 1rem;
    cursor: pointer;
    background: transparent;
    color: #96a6ba;
    opacity: 0.7;
    border: 1px solid #96a6ba;
  }

  .lang-switch button.selected {
    background: #4295f6;
    opacity: 1;
    color: #15161b;
    border: none;
  }

  .floating-image {
    display: none;
    position: fixed;
    cursor: pointer;
    width: 55px;
    height: auto;
    z-index: 999;
    transform: translateY(-50%);
    top: 20%;
    left: 0;
    scale: 1;
    transition: scale 0.3s ease;
  }

  .floating-image-mobile {
    display: none;
  }

  .floating-image:hover,
  .floating-image-mobile:hover {
    scale: 1.1;
  }

  .cta {
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 2rem;
  }

  .cta-image {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 50%;
  }

  .animate {
    animation: AnimatieKeyframe 3s ease-in-out infinite;
  }

  .amir-avatar {
    top: 20px;
    position: absolute;
    right: 17px;
  }

  .important-cta {
    font-size: 0.75rem;
    padding: 5px 15px;
    border-radius: 23px;
    border: 1px solid #91a0b4;
    margin-top: 4rem;
    line-height: 1.2rem;
  }

  @keyframes AnimatieKeyframe {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(6px);
    }
  }

  .modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: var(--backgroundColor);
    color: white;
    padding: 20px;
    border-radius: 10px;
    z-index: 9999;
    max-width: 350px;
    width: 100%;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .modal-content {
    margin: auto;
    padding: 22px;
    max-width: 500px;
    text-align: right;
  }

  .modal-button {
    display: block;
    flex: 1;
    background-color: #40e878;
    color: #000;
    border: none;
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1.2rem;
    text-align: center;
    font-family: "Vazir";
    text-decoration: none;
    transition: background-color 0.3s ease;
  }

  .modal-button:hover {
    background-color: #2dcf63;
  }

  .close-btn {
    color: #fff;
    font-size: 28px;
    font-weight: bold;
    position: absolute;
    top: 10px;
    right: 25px;
    cursor: pointer;
  }

  .close-btn:hover,
  .close-btn:focus {
    color: #f14b4b;
    text-decoration: none;
    cursor: pointer;
  }

  .persian {
    font-weight: 400;
    margin: 0;
    direction: rtl;
    font-family: "Vazir";
    font-size: 1.2rem;
  }

  section {
    margin-top: 20px;
  }

  code {
    color: var(--activeColor);
    font-family: var(--loRes);
    font-weight: 400;
  }

  .name {
    font-family: var(--loRes);
    font-size: 2.5rem;
    margin: 3rem 0 1.5rem 0;
    font-weight: 600;
    line-height: 3rem;
  }

  .title {
    margin-top: 3rem;
    display: flex;
    justify-content: space-between;
  }

  .slogan {
    color: var(--mutedTextColor) !important;
    font-size: 1.2rem !important;
    font-weight: 400 !important;
    margin: 0 !important;
  }

  .intro {
    margin: 0 0 10px 0;
  }

  h1,
  h2,
  h3 {
    font-family: var(--loRes);
    font-size: 2rem;
    font-weight: 600;
    color: var(--activeColor);
  }

  .logo {
    width: 10rem;
  }

  .desc {
    display: flex;
    justify-content: space-between;
    font-size: 0.75em;
    line-height: 1.5;
    color: var(--textColor);
  }

  .binary-code {
    font-family: var(--loRes);
    font-size: 0.7rem;
    color: var(--mutedTextColor);
    margin-bottom: -2rem;
    display: block;
  }

  .status-time {
    margin-left: 0.7em;
  }

  .time {
    color: var(--activeColor);
  }

  .blink {
    animation: blink 2s step-start 0s infinite;
  }

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }

  .code-container {
    max-width: 50vw;
    padding: 0px 20px;
    margin: 2.5rem auto;
    border: 1px solid #96a6ba !important;
    position: relative;
    border-radius: 20px;
    font-family: var(--loRes);
    font-weight: 400;
  }

  .code-container-p1 {
    margin: 1.5rem 0 0.5rem 0;
  }

  .based {
    position: absolute;
    display: inline;
    top: -32px;
    right: 20px;
    font-size: 0.8rem;
    background-color: #22272e;
    padding: 5px 9px;
    border-radius: 10px;
    border: 1px solid #1b1f23;
  }

  .based-footer {
    display: inline;
    font-size: 0.8rem;
    margin-top: 3rem;
    background-color: #22272e;
    padding: 5px 9px;
    border-radius: 10px;
    border: 1px solid #1b1f23;
  }

  .version {
    text-align: right;
    font-size: 0.6em;
  }

  .version a {
    font-size: 0.7rem;
    color: var(--textColor);
    text-decoration: none;
    background-color: #1b1f23;
    padding: 7px 12px;
    border-radius: 20px;
  }

  .switch {
    display: flex;
    gap: 1.5rem;
  }

  /* ===========================
   Footer
   =========================== */

  .links-footer {
    display: flex;
    gap: 2rem;
  }

  .site-footer {
    margin-top: 0.5rem;
    padding: 2rem 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--textColor);
    font-size: 0.7rem;
    line-height: 1.2rem;
  }

  .footer-left {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .footer-sep {
    opacity: 0.4;
    margin: 0 0.1rem;
  }

  .footer-right {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    text-decoration: none;
    color: var(--textColor);
    font-weight: 500;
    transition: opacity 0.2s ease;
  }

  .footer-right:hover {
    opacity: 0.7;
  }

  .footer-ig {
    width: 16px;
    height: 17px;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    .code-container {
      max-width: 85vw;
    }
    .modal {
      max-width: 300px;
    }

    .name {
      font-size: 1.7rem;
    }

    h1,
    h2,
    h3 {
      font-size: 1.7rem;
    }

    .amir-avatar {
      top: 24px;
    }
    .switch {
      gap: 1rem;
    }

    .lang-switch button {
      padding: 0.2rem 0.4rem;
      font-size: 0.8rem;
    }

    .m-1 {
      width: 240px;
    }

    .theme-toggle__track {
      padding: 0.4rem 1.5rem;
    }

    .theme-toggle__thumb {
      width: 15px;
      height: 15px;

      top: 7px;
    }

    .theme-toggle__icon.theme-toggle__icon--right {
      width: 15px;
      height: 15px;
      top: 7px;
    }

    .theme-toggle__icon.theme-toggle__icon--left {
      width: 14px;
      height: 14px;
      top: 7px;
    }

    .theme-toggle__thumb--light {
      transform: translateX(35px);
      left: -7px;
    }

    .insta-verify {
      width: 14px;
      height: 14px;
      top: -0.6rem;
      margin-left: 0.3rem;
    }

    .marquee {
      --speed: 100s;
    }

    .logo {
      width: 8rem;
    }

    .floating-image {
      display: none;
    }
    .floating-image-mobile {
      display: none;
      position: fixed;
      cursor: pointer;
      width: 55px;
      height: auto;
      z-index: 999;
      transform: translateY(-50%);
      top: 5.5rem;
      right: 0;
      scale: 1;
      transition: scale 0.3s ease;
    }

    .banner {
      margin-left: -10%;
    }
  }
</style>
